import { Dropdown, DropdownProps } from "primereact/dropdown";
import {
  CARET,
  CLICKABLE,
  DISABLED,
  OPT_DIM,
  OPT_FOCUSED,
  OPT_LIST,
  OPT_ROW,
  OPT_SELECTED,
  OPT_UL,
  SEL_BASE,
  SEL_VALUE,
} from "../styles";

/**
 * Everything Dropdown understands, minus the three props this wrapper owns outright,
 * plus the one styling decision the design makes. Intersecting DropdownProps rather
 * than re-listing its props keeps `options`, the templates and `onChange` at
 * PrimeReact's own types — and means a Dropdown feature this dock has not needed yet
 * (filter, option groups, virtual scrolling) works without touching this file.
 */
type Props = Omit<DropdownProps, "unstyled" | "pt" | "dropdownIcon"> & {
  /** SEL_MIN_BAR today; a second placement would bring its own width */
  minWidthClass: string;
};

/**
 * The dock's combobox: a PrimeReact Dropdown in `unstyled` mode, dressed entirely
 * from ../styles through its `pt` map.
 *
 * PrimeReact is here for the behaviour, not the looks — arrow keys, Home/End,
 * typeahead, Escape, role="listbox"/"option", aria-expanded, outside-click and
 * scroll dismissal, and overlay positioning. The panel goes to <body> (Dropdown's
 * own default), which both keeps it clear of the panel's scroll box and lets
 * PrimeReact flip it above the trigger; the dock lives on the bottom edge of the
 * viewport, so it always does.
 *
 * Anything not named here reaches the root element on its own: PrimeReact merges the
 * props it does not recognise onto it, with pt.root applied last.
 */
export function Select({ minWidthClass, disabled = false, ...rest }: Props) {
  return (
    <Dropdown
      unstyled
      disabled={disabled}
      {...rest}
      dropdownIcon={<>&#9662;</>}
      pt={{
        root: { className: `${SEL_BASE} ${minWidthClass} ${disabled ? DISABLED : CLICKABLE}` },
        input: { className: SEL_VALUE },
        trigger: { className: CARET },
        panel: { className: OPT_LIST },
        list: { className: OPT_UL },
        // context.selected / .focused are PrimeReact's; the tones are the design's
        item: ({ context }: { context: { selected: boolean; focused: boolean } }) => ({
          className: `${OPT_ROW} ${context.selected ? OPT_SELECTED : ""} ${
            context.focused ? OPT_FOCUSED : ""
          }`,
        }),
        emptyMessage: { className: `${OPT_ROW} ${OPT_DIM}` },
      }}
    />
  );
}
